import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import gun from '../config/gun.config';
import { Pin, REACTION_KEYS, ReactionEmoji, PIN_LIFETIME_MS } from '../types/pin';
import { ActivityCategory, AvatarConfig } from '../types/user';

function parseNum(val: any, fallback: number): number {
  if (typeof val === 'number') return val;
  const n = Number(val);
  return isNaN(n) ? fallback : n;
}

function parseAvatar(raw: any): any {
  if (!raw) return undefined;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return undefined; }
}

export function usePins() {
  const [pins, setPins] = useState<Record<string, Pin>>({});
  const pendingRef = useRef<Record<string, Pin>>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sigRef   = useRef<Record<string, string>>({});

  useEffect(() => {
    const node = gun.get('stabeon_pins');
    const mapNode = node.map();

    mapNode.on((data: any, id: string) => {
      if (!data || !id) return;
      const createdAt = parseNum(data.createdAt, 0);
      if (!createdAt) return;

      const sig = `${data.text}|${data.expiresAt}|${data.reactions_fire||0}|${data.reactions_heart||0}|${data.reactions_skull||0}|${data.reactions_hundred||0}|${data.reactions_eyes||0}|${data.commentCount||0}`;
      if (sigRef.current[id] === sig) return;
      sigRef.current[id] = sig;

      const pin: Pin = {
        id,
        creatorId: data.creatorId || '',
        creatorNick: data.creatorNick || 'Anonymous',
        creatorAvatar: parseAvatar(data.creatorAvatar),
        lat: parseNum(data.lat, 0),
        lng: parseNum(data.lng, 0),
        text: data.text || '',
        category: data.category || 'other',
        imageData: (data.imageData && data.imageData !== 'null') ? data.imageData : undefined,
        videoData: (data.videoData && data.videoData !== 'null') ? data.videoData : undefined,
        videoThumbnail: (data.videoThumbnail && data.videoThumbnail !== 'null') ? data.videoThumbnail : undefined,
        mediaType: data.mediaType || (data.videoData ? 'video' : 'image'),
        markerImage: (data.markerImage && data.markerImage !== 'null') ? data.markerImage : undefined,
        locationName: (data.locationName && data.locationName !== 'null') ? data.locationName : undefined,
        createdAt,
        expiresAt: parseNum(data.expiresAt, createdAt + PIN_LIFETIME_MS),
        reactions_fire: parseNum(data.reactions_fire, 0),
        reactions_heart: parseNum(data.reactions_heart, 0),
        reactions_skull: parseNum(data.reactions_skull, 0),
        reactions_hundred: parseNum(data.reactions_hundred, 0),
        reactions_eyes: parseNum(data.reactions_eyes, 0),
        commentCount: parseNum(data.commentCount, 0),
      };

      // Skip if lat/lng are zero (invalid)
      if (!pin.lat && !pin.lng) return;

      pendingRef.current[id] = pin;
      if (!timerRef.current) {
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          const batch = pendingRef.current;
          pendingRef.current = {};
          setPins(prev => Object.assign({}, prev, batch));
        }, 50);
      }
    });

    return () => {
      try { mapNode.off(); } catch (_) {}
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };
  }, []);

  // Filter out expired pins
  const activePins = useMemo(
    () => Object.values(pins).filter(p => p.expiresAt > Date.now()),
    [pins]
  );

  const createPin = useCallback((
    lat: number,
    lng: number,
    text: string,
    category: ActivityCategory,
    creatorId: string,
    creatorNick: string,
    creatorAvatar?: AvatarConfig,
    imageData?: string,
    markerImage?: string,
    videoData?: string,
    videoThumbnail?: string,
    locationName?: string,
  ) => {
    const now = Date.now();
    const pinData: Record<string, any> = {
      creatorId,
      creatorNick,
      creatorAvatar: creatorAvatar ? JSON.stringify(creatorAvatar) : null,
      lat,
      lng,
      text: text.trim(),
      category,
      imageData: imageData || null,
      videoData: videoData || null,
      videoThumbnail: videoThumbnail || null,
      mediaType: videoData ? 'video' : 'image',
      markerImage: markerImage || null,
      locationName: locationName || null,
      createdAt: now,
      expiresAt: now + PIN_LIFETIME_MS,
      reactions_fire: 0,
      reactions_heart: 0,
      reactions_skull: 0,
      reactions_hundred: 0,
      reactions_eyes: 0,
      commentCount: 0,
    };

    // Use a deterministic key so the listener picks it up correctly without duplicate records
    const pinId = `pin_${now}_${creatorId.slice(-6)}`;
    gun.get('stabeon_pins').get(pinId).put(pinData as any);
  }, []);

  const reactToPin = useCallback((pinId: string, emoji: ReactionEmoji, userId: string) => {
    const key = REACTION_KEYS[emoji];
    const reactionField = `reactions_${key}`;
    const userReactedField = `reacted_${key}_${userId}`;
    const pinNode = gun.get('stabeon_pins').get(pinId);

    pinNode.get(userReactedField).once((val: any) => {
      if (val) {
        pinNode.get(reactionField).once((count: any) => {
          pinNode.get(reactionField).put(Math.max(0, (parseNum(count, 1)) - 1));
        });
        pinNode.get(userReactedField).put(null);
      } else {
        pinNode.get(reactionField).once((count: any) => {
          pinNode.get(reactionField).put((parseNum(count, 0)) + 1);
        });
        pinNode.get(userReactedField).put(true);
      }
    });
  }, []);

  const hasUserReacted = useCallback((pinId: string, emoji: ReactionEmoji, userId: string): Promise<boolean> => {
    return new Promise(resolve => {
      const key = REACTION_KEYS[emoji];
      gun.get('stabeon_pins').get(pinId).get(`reacted_${key}_${userId}`).once((val: any) => {
        resolve(!!val);
      });
    });
  }, []);

  const deletePin = useCallback((pinId: string, creatorId: string, myId: string) => {
    if (creatorId !== myId) return; // Only creator can delete
    // Mark as expired (effectively deletes it)
    gun.get('stabeon_pins').get(pinId).put({ expiresAt: 0 } as any);
    setPins(prev => {
      const next = { ...prev };
      delete next[pinId];
      return next;
    });
  }, []);

  return { pins: activePins, createPin, reactToPin, hasUserReacted, deletePin };
}
